import firebase from 'firebase/app';
import 'firebase/storage';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { projectFirestore } from '../../../../firebase-config/config';
import { useAuthContext } from '../../../../firebase-files/hooks/useAuthContext';
import { useFirestore } from '../../../../firebase-files/hooks/useFirestore';
import './PlantationTwoVideo.scss';

const PlantationTwoAllVideo = () => {
  const [videos, setVideos] = useState([]);
  const { user } = useAuthContext();
  const { deleteDocument } = useFirestore('videos');
  const [showModal, setShowModal] = useState(false);
  const [uploadType, setUploadType] = useState('url');
  const [formData, setFormData] = useState({
    videoUrl: '',
    title: '',
    subHeading: '',
    categories: '',
    platform: '',
    file: null,
    docId: '',
    status: true,
  });
  const [previewUrl, setPreviewUrl] = useState('');
  const [touched, setTouched] = useState({});
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    id: null,
  });

  const fetchVideos = async () => {
    try {
      const snapshot = await projectFirestore.collection('videos').get();
      const data = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        status: doc.data().status !== false
      }));
      setVideos(data);
    } catch (error) {
      console.error("Error fetching videos:", error);
      // alert("Failed to load videos");
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    const updatedValue = type === 'file' ? files[0] : value;
    setFormData((prev) => ({ ...prev, [name]: updatedValue }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    if (name === 'file' && files[0]) {
      setPreviewUrl(URL.createObjectURL(files[0]));
    }
  };

  const isFieldValid = (field) => {
    if (uploadType === 'url' && field === 'videoUrl') return isValidUrl(formData.videoUrl);
    // ==== START: Upload File Field Validation (Commented) ====
    // if (uploadType === 'upload' && field === 'file') return formData.file instanceof File;
    // ==== END: Upload File Field Validation (Commented) ====
    return formData[field]?.toString().trim() !== '';
  };

  const renderError = (field) => {
    if (!touched[field] || isFieldValid(field)) return null;
    if (field === 'videoUrl') return <span className="error-message">Please enter a valid URL</span>;
    // ==== START: Upload File Error Message (Commented) ====
    // if (field === 'file') return <span className="error-message">Please upload a video file</span>;
    // ==== END: Upload File Error Message (Commented) ====
    return <span className="error-message">This field is required</span>;
  };

  const isFormValid = () => {
    const required = ['title', 'categories', 'platform'];
    const specific = uploadType === 'url' ? 'videoUrl' : 'file';
    return [...required, specific, 'docId'].every(isFieldValid);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    try {
      let videoUrl = formData.videoUrl;
      let fileName = '';

      // ==== START: Upload File Upload Logic (Commented) ====
      // if (uploadType === 'upload' && formData.file && formData.docId) {
      //   const storageRef = firebase.storage().ref(`videos/${formData.docId}/${formData.file.name}`);
      //   await storageRef.put(formData.file);
      //   videoUrl = await storageRef.getDownloadURL();
      //   fileName = formData.file.name;
      // }
      // ==== END: Upload File Upload Logic (Commented) ====

      const videoData = {
        videoUrl,
        title: formData.title,
        subHeading: formData.subHeading || '',
        categories: formData.categories,
        platform: formData.platform,
        fileName,
        status: formData.status,
        views: 0,
        createdBy: user?.uid || null,
        createdAt: new Date(),
        lastUpdated: new Date()
      };

      await projectFirestore.collection('videos').doc(formData.docId).update(videoData);

      // alert('Video updated successfully!');
      setFormData({ 
        videoUrl: '', 
        title: '', 
        subHeading: '', 
        categories: '', 
        platform: '', 
        file: null, 
        docId: '', 
        status: true 
      });
      setPreviewUrl('');
      setTouched({});
      setShowModal(false);
      fetchVideos();
    } catch (error) {
      console.error('Update failed:', error);
      // alert('Update failed. Please try again.');
    }
  };

  const getInstagramPostId = (url) => {
    const match = url.match(/(?:https?:\/\/)?(?:www\.)?instagram\.com(?:\/[^\/]+)?\/(p|reel)\/([^\/?#&]+)/);
    return match ? match[2] : '';
  };

  const renderVideoEmbed = (video) => {
    if (video.platform === 'YouTube') {
      const embedUrl = video.videoUrl.includes('/embed/')
        ? video.videoUrl
        : video.videoUrl.replace('/watch?v=', '/embed/').replace('/shorts/', '/embed/');
      return (
        <iframe
          src={embedUrl}
          width="100%"
          height="100%"
          allow="autoplay; encrypted-media"
          allowFullScreen
          title={video.title}
        />
      );
    } else if (video.platform === 'Instagram') {
      const postId = getInstagramPostId(video.videoUrl);
      return postId ? (
        <div className="instagram-embed-container">
          <blockquote
            className="instagram-media"
            data-instgrm-permalink={`https://www.instagram.com/p/${postId}/`}
            data-instgrm-version="14"
            style={{ width: '100%', height: '100%' }}
          >
            <a href={`https://www.instagram.com/p/${postId}/`}>View on Instagram</a>
          </blockquote>
        </div>
      ) : (
        <div className="instagram-error">Invalid Instagram URL</div>
      );
    }

    return (
      <video
        src={video.videoUrl}
        width="100%"
        height="100%"
        controls
        style={{ objectFit: 'contain' }}
        preload="none"
        playsInline
      />
    );
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation.id) return;

    try {
      const docRef = projectFirestore.collection('videos').doc(deleteConfirmation.id);
      const docSnap = await docRef.get();

      if (!docSnap.exists) {
        // alert('Video not found.');
        return;
      }

      const videoData = docSnap.data();

      if (videoData.platform === 'Local' && videoData.fileName) {
        const storageRef = firebase.storage().ref(`videos/${deleteConfirmation.id}`);
        await storageRef.listAll().then(dir => {
          const deletePromises = dir.items.map(fileRef => fileRef.delete());
          return Promise.all(deletePromises);
        });
      }

      await deleteDocument(deleteConfirmation.id);
      fetchVideos();
      // alert('Video deleted successfully!');
    } catch (error) {
      console.error('Error deleting video:', error);
      // alert('Failed to delete video.');
    } finally {
      setDeleteConfirmation({ show: false, id: null });
    }
  };

  const handleEdit = (video) => {
    setFormData({
      videoUrl: video.videoUrl,
      title: video.title,
      subHeading: video.subHeading || '',
      categories: video.categories,
      platform: video.platform,
      file: null,
      docId: video.id,
      status: video.status !== undefined ? video.status : true,
    });
    setUploadType(video.platform === 'Local' ? 'upload' : 'url');
    setPreviewUrl(video.platform === 'Local' ? video.videoUrl : '');
    setTouched({});
    setShowModal(true);
  };

  const getVisibleVideos = () => {
    if (user?.role === 'admin') return videos;
    return videos.filter(video => video.status);
  };

  return (
    <section className="video-section">
      <h2>All Videos</h2>

      <div className="video-reel-wrapper">
        {getVisibleVideos().map((video) => (
          <div 
            className={`reel-card ${video.status === false ? 'inactive-video' : ''}`} 
            key={video.id}
          >
            {user?.role === 'admin' && video.status === false && (
              <div className="inactive-badge">Inactive</div>
            )}

            <div className="thumbnail-container">
              <div className="video-embed-wrapper">
                {renderVideoEmbed(video)}
              </div>

              {user?.role === 'admin' && (
                <>
                  <div className="edit-icon" onClick={(e) => { e.stopPropagation(); handleEdit(video); }}>✏️</div>
                  <div className="delete-icon" onClick={(e) => { 
                    e.stopPropagation(); 
                    setDeleteConfirmation({ show: true, id: video.id });
                  }}>
                    <Trash2 />
                  </div>
                </>
              )}
            </div>

            <div className="video-info">
              <h4>{video.title}</h4>
              <p className="subheading">{video.subHeading}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Add Video Button (Admin Only) */}
      {user?.role === 'admin' && (
        <div className="add-video-btn-wrapper">
          <button 
            className="add-video-btn" 
            onClick={() => {
              const newDocRef = projectFirestore.collection('videos').doc();
              setFormData(prev => ({ ...prev, docId: newDocRef.id, status: true }));
              newDocRef.set({ createdAt: new Date(), status: true });
              setShowModal(true);
            }}
          >
            Add Video
          </button>
        </div>
      )}

      {/* Modal Form */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{formData?.docId && videos.find(v => v.id === formData.docId) ? 'Edit Video' : 'Add New Video'}</h3>

            {/* ==== START: Upload Type Toggle (Commented Upload) ==== */}
            <div className="toggle-upload-type">
              <button 
                onClick={() => setUploadType('url')} 
                className={uploadType === 'url' ? 'active' : ''}
              >
                Video URL
              </button>
              {/* 
              <button 
                onClick={() => setUploadType('upload')} 
                className={uploadType === 'upload' ? 'active' : ''}
              >
                Upload File
              </button> 
              */}
            </div>
            {/* ==== END: Upload Type Toggle ==== */}

            <form className="video-form" onSubmit={handleSubmit}>
              {uploadType === 'url' && (
                <label className={touched.videoUrl && !isFieldValid('videoUrl') ? 'error' : ''}>
                  Video URL:
                  <input 
                    type="text" 
                    name="videoUrl" 
                    placeholder="Enter video URL" 
                    value={formData.videoUrl} 
                    onChange={handleChange} 
                  />
                  {renderError('videoUrl')}
                </label>
              )}

              {/* ==== START: Upload File Input (Commented) ==== */}
              {/*
              {uploadType === 'upload' && (
                <label className={touched.file && !isFieldValid('file') ? 'error' : ''}>
                  Upload Video:
                  <input 
                    type="file" 
                    name="file" 
                    accept="video/*" 
                    onChange={handleChange} 
                  />
                  {renderError('file')}
                </label>
              )}
              */}
              {/* ==== END: Upload File Input ==== */}

              {/* Preview Section */}
              {(uploadType === 'url' && isValidUrl(formData.videoUrl)) || 
              (uploadType === 'upload' && previewUrl) ? (
                <div className="preview-card">
                  <div className="thumbnail-container">
                    {uploadType === 'url'
                      ? renderVideoEmbed(formData)
                      : <video 
                          src={previewUrl} 
                          controls 
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                          preload="none" 
                          playsInline 
                        />}
                  </div>
                  <div className="video-info">
                    <h4>{formData.title || 'Preview Title'}</h4>
                    <p className="subheading">{formData.subHeading || 'Preview Subheading'}</p>
                  </div>
                </div>
              ) : null}

              {/* Text Fields */}
              <label className={touched.title && !isFieldValid('title') ? 'error' : ''}>
                Title:
                <input 
                  type="text" 
                  name="title" 
                  placeholder="Enter title" 
                  value={formData.title} 
                  onChange={handleChange} 
                />
                {renderError('title')}
              </label>

              <label>
                Sub Heading:
                <input 
                  type="text" 
                  name="subHeading" 
                  placeholder="Enter sub heading" 
                  value={formData.subHeading} 
                  onChange={handleChange} 
                />
              </label>

              <label className={touched.categories && !isFieldValid('categories') ? 'error' : ''}>
                Categories:
                <input 
                  type="text" 
                  name="categories" 
                  placeholder="Enter categories" 
                  value={formData.categories} 
                  onChange={handleChange} 
                />
                {renderError('categories')}
              </label>

              <label className={touched.platform && !isFieldValid('platform') ? 'error' : ''}>
                Platform:
                <select 
                  name="platform" 
                  value={formData.platform} 
                  onChange={handleChange}
                >
                  <option value="">Select Platform</option>
                  <option value="YouTube">YouTube</option>
                  <option value="Instagram">Instagram</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Local">Uploaded Video</option>
                </select>
                {renderError('platform')}
              </label>

              {/* Status Switch */}
              <label>
                Status:
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    name="status"
                    checked={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                    id="status-toggle"
                  />
                  <label htmlFor="status-toggle" className="toggle-slider"></label>
                  <span style={{ 
                    color: formData.status ? '#1f4f2c' : '#ff6b6b',
                    fontWeight: 'bold'
                  }}>
                    {formData.status ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </label>

              <div className="form-buttons">
                <button 
                  type="submit" 
                  className={!isFormValid() ? 'disabled' : ''} 
                  disabled={!isFormValid()}
                >
                  Save
                </button>
                <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmation.show && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirmation">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this video?</p>
            <div className="confirmation-buttons">
              <button 
                className="cancel-btn"
                onClick={() => setDeleteConfirmation({ show: false, id: null })}
              >
                Cancel
              </button>
              <button 
                className="delete-btn"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PlantationTwoAllVideo;